<template>
	<a
		ref="elRef"
		:href="absoluteUrl"
		:target="target"
		:class="{
			exactActiveClass: exactActiveClass && route.path === normalizedRawLocation.route.value.path,
			'router-link-active': route.path === normalizedRawLocation.route.value.path,
			'router-is-active': normalizedRawLocation.isActive.value,
			'router-is-exact-active': normalizedRawLocation.isExactActive.value,
		}"
		@click="handleClick"
	>
		<slot />
	</a>
</template>

<script setup lang="ts">
	import type {RouteLocationRaw} from 'vue-router';

	interface Props {
		to?: MaybeRefOrGetter<RouteLocationRaw>;
		href?: MaybeRefOrGetter<RouteLocationRaw>;
		exactActiveClass?: string;
		target?: string;
	}

	const props = defineProps<Props>();
	const route = useRoute();
	const router = useRouter();
	const nuxtApp = useNuxtApp();
	const elRef = ref(null);

	const routeLocation = computed(() => {
		const loc = toValue(props.to || props.href || '');

		if (typeof loc !== 'string') {
			return loc;
		}

		if (loc.startsWith('#')) {
			return {hash: loc};
		}

		// Handle URLs with hash anchors (e.g., "/page#section")
		const hashIndex = loc.indexOf('#');
		const path = hashIndex > -1 ? loc.substring(0, hashIndex) : loc;
		const hash = hashIndex > -1 ? loc.substring(hashIndex) : undefined;

		return {
			path: ensureLeadingSlash(
				removeTrailingSlash(dropUnderscoreRouteParts(path)),
			),
			...(hash && {hash}),
		};
	});

	const isExternal = computed(() => !!props.target && props.target !== '_self');

	const rawLocation = computed(() =>
		isExternal.value
			? toValue(props.to || props.href || '#')
			: routeLocation.value,
	);

	const normalizedRawLocation = useLink({to: rawLocation});

	const absoluteUrl = computed(() =>
		typeof rawLocation.value === 'string'
			? rawLocation.value
			: `${useBaseUrl()}${normalizedRawLocation.href.value}`,
	);

	const {stop: stopObserver} = useIntersectionObserver(
		elRef,
		([{isIntersecting}]) => {
			if (!isIntersecting) {
				return;
			}

			stopObserver();

			Promise.resolve(
				nuxtApp.hooks.callHook(
					'link:prefetch',
					router.resolve(routeLocation.value).fullPath,
				),
			).catch(() => {
			});

			if (!isExternal.value) {
				preloadRouteComponents(routeLocation.value, router).catch(() => {
				});
			}
		},
	);

	function handleClick(e: MouseEvent) {
		if (isExternal.value) {
			return;
		}

		e.preventDefault();

		navigateTo(routeLocation.value);
	}
</script>
